/**
 * GenerateResponseFlow — the whole "generate this notice's response"
 * conversation, in one place (extracted 05-10-2026).
 *
 * WHY THIS EXISTS
 * ---------------
 * Generating a response is not one call, it is a three-step exchange with the
 * engine:
 *
 *   1. Terms, and the choice of whether to identify legal issues.
 *   2. If issues were identified, which of them (plus any the user adds).
 *   3. If the engine says something is missing, answer it or waive it.
 *
 * All of that, with its async polling and its many failure branches, used to
 * live inside the subscriber's notice form (EPForm.jsx) as ~600 lines of
 * handlers plus three modals. When the NABS reviewer needed the SAME
 * conversation, copying it would have meant two implementations of a flow
 * that is already hard to keep correct, so it was lifted out here and BOTH
 * pages render this component.
 *
 * WHAT IS PARAMETERISED
 * ---------------------
 * Only the wire: `generateUrl` / `statusUrl` and the `identity` fields each
 * caller must send with every request (the subscriber sends the doctype and
 * document name; the reviewer's request id is already in its URL). Both
 * endpoints answer in the same shape - `{success, data}` where `data` carries
 * `status: "needs_input" | "needs_selection"`, or `task_id` for the queued
 * path, or the finished result - which is what lets one component speak to
 * both.
 *
 * WHAT THE PAGE STILL OWNS
 * ------------------------
 * What to DO with the result: `onApplied` hands it back, so the subscriber's
 * form writes the draft into its editor while the reviewer's page takes the
 * reviewer into the review. The flow itself never writes a draft anywhere.
 *
 * Props
 *   open                 show the terms step (the flow's entry point)
 *   onClose              user cancelled or the flow finished
 *   legalChoiceEnabled   offer the Legal Issue Identification choice
 *   generateUrl          POST endpoint
 *   statusUrl            (taskId) => GET endpoint for the queued path
 *   identity             fields merged into every request body
 *   resumeKey            localStorage key, so a reload resumes a queued run
 *   beforeGenerate       awaited before the first call (e.g. save the form)
 *   onApplied            (result, {toastKind}) => void
 *   onSubscriptionError  (text) => void; falls back to a toast when absent
 *   afterSuccess         called once a draft came back
 */
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Input,
  Modal,
  Radio,
  Row,
  Space,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getData, postResponseData } from "../../api/apiService";
import TermsAndConditions from "../../pages/TermsAndConditions";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Only a real draft belongs in a Response Message editor. `message` used to be
// accepted as a last resort, which is how the backend's internal "Fallback
// requires query selection" text ended up in the editor (with a success
// toast) whenever the AI provider was unavailable - seen on production
// 29-09-2026 when the provider account ran out of credit. Returns "" when
// there is no draft so the caller shows the failure instead.
export const draftFromResult = (r) => (r && (r.response_message || r.response)) || "";

// A result counts as a success when the engine produced text, OR when Review
// Mode deliberately held that text back (it strips the text and sets
// review_pending). Testing only for text is how Review Mode's own generations
// were reported as failures on two of the subscriber's paths.
const resultIsUsable = (r) =>
  Boolean(r && (r.review_pending || r.review_session_id || draftFromResult(r)));

// FastAPI answers {detail}, the legacy backend {success:false, error}. Read
// both so the user sees the real reason instead of a generic failure.
const errorTextOf = (err, fallback) =>
  (typeof err === "string" && err) ||
  err?.detail ||
  err?.error ||
  err?.message ||
  fallback;

const looksLikePlanLimit = (text) =>
  typeof text === "string" &&
  (text.toLowerCase().includes("plan") ||
    text.toLowerCase().includes("limit reached"));

const GenerateResponseFlow = ({
  open,
  onClose,
  legalChoiceEnabled = false,
  generateUrl,
  statusUrl,
  identity = {},
  resumeKey = null,
  beforeGenerate,
  onApplied,
  onSubscriptionError,
  afterSuccess,
}) => {
  // Step 1 — terms + the legal-issue choice.
  const [accepted, setAccepted] = useState(false);
  const [withLegal, setWithLegal] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Step 2 — the identified issues, and any the user writes.
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [issuesData, setIssuesData] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [customIssues, setCustomIssues] = useState([]);
  const [newCustomIssue, setNewCustomIssue] = useState("");
  const [issuesLoading, setIssuesLoading] = useState(false);

  // Step 3 — what the engine says is missing.
  const [gapsOpen, setGapsOpen] = useState(false);
  const [gaps, setGaps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [waived, setWaived] = useState([]);
  const [gapsLoading, setGapsLoading] = useState(false);

  // Queued path.
  const [pollStage, setPollStage] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollSafetyRef = useRef(null);
  const pollErrorsRef = useRef(0);

  const identityRef = useRef(identity);
  identityRef.current = identity;

  // Consent is per generation, not per session: every time the terms step
  // opens, the tick starts clear. antd would otherwise keep the previous
  // tick and the next run would start already consented.
  useEffect(() => {
    if (open) setAccepted(false);
  }, [open]);

  const reportError = (err, fallback) => {
    if (err?.network) {
      toast.error(
        "Network problem - the request never reached the server. " +
          "Check your internet connection and try again; " +
          "nothing you entered has been lost.",
        { position: "top-right", autoClose: 8000 },
      );
      return;
    }
    const text = errorTextOf(err, fallback);
    if (looksLikePlanLimit(text) && onSubscriptionError) {
      onSubscriptionError(text);
      return;
    }
    toast.error(text, { position: "top-right", autoClose: 6000 });
  };

  const post = (extra) =>
    postResponseData(generateUrl, { ...identityRef.current, ...extra });

  // Entries must reach the backend as {action, data}, not bare strings
  // (enhanced_response._merge_user_data). An empty answer means "later".
  const wrapAnswers = () =>
    Object.entries(answers).reduce((acc, [point, value]) => {
      const trimmed = (value || "").toString().trim();
      acc[point] = trimmed
        ? { action: "provided", data: trimmed }
        : { action: "will_submit_later" };
      return acc;
    }, {});

  const resetGaps = () => {
    setGaps([]);
    setAnswers({});
    setWaived([]);
  };

  const closeEverything = () => {
    setIssuesOpen(false);
    setGapsOpen(false);
    setIssuesData(null);
    setSelectedIssues([]);
    setCustomIssues([]);
    setNewCustomIssue("");
    resetGaps();
    setAccepted(false);
    setWithLegal(false);
    onClose?.();
  };

  // Every terminal success lands here, so there is one place that tells the
  // page a draft exists and one place that tidies up after it. `refetch` is
  // only set on the paths that reloaded the record before this was extracted
  // (the issue-picker submit and the queued run), so the page is not newly
  // refetched underneath a plain generation.
  const finish = (result, toastKind, { refetch = false } = {}) => {
    onApplied?.(result, { toastKind });
    closeEverything();
    if (refetch) afterSuccess?.();
  };

  // --- Step 1 -------------------------------------------------------------
  const startGeneration = async () => {
    setGenerating(true);
    try {
      await beforeGenerate?.();
    } catch (err) {
      setGenerating(false);
      reportError(err, "The notice could not be saved. Please try again.");
      return;
    }
    try {
      const data = await post({ with_legal: withLegal });
      const payload = data?.data || {};

      // Moving on to a question closes the terms step - the page owns that
      // modal's `open`, so the flow has to hand it back or the two stack.
      if (payload.status === "needs_input" && payload.insufficient_queries) {
        setGaps(payload.insufficient_queries);
        setAnswers({});
        setWaived([]);
        setGapsOpen(true);
        setGenerating(false);
        onClose?.();
        return;
      }
      if (payload.status === "needs_selection") {
        setIssuesData(
          typeof payload.response === "string"
            ? JSON.parse(payload.response)
            : payload.response,
        );
        setIssuesOpen(true);
        setGenerating(false);
        onClose?.();
        return;
      }
      if (payload.task_id) {
        startPolling(payload.task_id);
        setGenerating(false);
        onClose?.();
        return;
      }
      if (!resultIsUsable(payload)) {
        toast.error(
          payload.message ||
            "The response could not be generated. Please try again in a few minutes.",
          { position: "top-right", autoClose: 8000 },
        );
        setGenerating(false);
        return;
      }
      setGenerating(false);
      finish(payload, "info");
    } catch (err) {
      setGenerating(false);
      reportError(err, "Network Error. Please retry!");
      onClose?.();
    }
  };

  // --- Step 2 -------------------------------------------------------------
  const addCustomIssue = () => {
    if (!newCustomIssue.trim()) return;
    setCustomIssues((prev) => [
      ...prev,
      {
        custom_issue: newCustomIssue.trim(),
        is_custom_issue: true,
        uid: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      },
    ]);
    setNewCustomIssue("");
  };

  const editCustomIssue = (uid, value) =>
    setCustomIssues((prev) =>
      prev.map((i) => (i.uid === uid ? { ...i, custom_issue: value } : i)),
    );

  const removeCustomIssue = (uid) =>
    setCustomIssues((prev) => prev.filter((i) => i.uid !== uid));

  const toggleIssue = (uid, checked) => {
    if (checked) {
      const picked = (issuesData?.issues || []).find((i) => i.uid === uid);
      if (picked) setSelectedIssues((prev) => [...prev, picked]);
    } else {
      setSelectedIssues((prev) => prev.filter((i) => i.uid !== uid));
    }
  };

  const totalSelected =
    selectedIssues.length +
    customIssues.filter((i) => i.custom_issue.trim()).length;

  const generateWithSelectedIssues = async () => {
    setIssuesLoading(true);
    try {
      const typed = customIssues
        .filter((i) => i.custom_issue.trim() !== "")
        // eslint-disable-next-line no-unused-vars
        .map(({ uid, ...rest }) => rest);

      const data = await post({
        with_legal: true,
        custom_legal_issues: { issues: [...selectedIssues, ...typed] },
        user_provided_data: wrapAnswers(),
        proceed_with_available: true,
      });

      // Queued path: the engine's full drafting run outlives an HTTP request.
      if (data?.success && data?.data?.task_id) {
        startPolling(data.data.task_id);
        return;
      }
      const result = data?.data || {};
      if (!resultIsUsable(result)) {
        setIssuesLoading(false);
        toast.error(
          result.message ||
            "The response could not be generated. Please try again in a few minutes.",
          { position: "top-right", autoClose: 8000 },
        );
        return;
      }
      setIssuesLoading(false);
      finish(result, "success", { refetch: true });
    } catch (err) {
      // The selections survive: only success clears them, so a retry is safe.
      setIssuesLoading(false);
      reportError(err, "Failed to generate response with selected issues!");
    }
  };

  // --- Step 3 -------------------------------------------------------------
  const toggleWaive = (point) =>
    setWaived((prev) => {
      if (prev.includes(point)) return prev.filter((p) => p !== point);
      setAnswers((prevAnswers) => {
        const next = { ...prevAnswers };
        delete next[point];
        return next;
      });
      return [...prev, point];
    });

  const answerGap = (point, value) => {
    setAnswers((prev) => ({ ...prev, [point]: value }));
    if (value && waived.includes(point)) {
      setWaived((prev) => prev.filter((p) => p !== point));
    }
  };

  // Both buttons on the gaps step do the same call with a different body, so
  // they share one submit rather than drifting apart.
  const submitGaps = async ({ waiveAll = false } = {}) => {
    setGapsLoading(true);
    try {
      const data = await post(
        waiveAll
          ? { with_legal: withLegal, proceed_with_available: true }
          : {
              with_legal: withLegal,
              user_provided_data: wrapAnswers(),
              proceed_with_available: waived.length > 0,
            },
      );
      const result = data?.data || {};

      if (result.status === "needs_input" && result.insufficient_queries) {
        setGaps(result.insufficient_queries);
        setGapsLoading(false);
        return;
      }
      if (result.status === "needs_selection") {
        setIssuesData(
          typeof result.response === "string"
            ? JSON.parse(result.response)
            : result.response,
        );
        setGapsOpen(false);
        setIssuesOpen(true);
        setGapsLoading(false);
        // The answers are carried into the issues step on purpose - the final
        // submit sends them with the chosen issues. Only the modal's own
        // state is cleared.
        setGaps([]);
        setWaived([]);
        return;
      }
      if (result.task_id) {
        setGapsOpen(false);
        setGapsLoading(false);
        startPolling(result.task_id);
        return;
      }
      if (!resultIsUsable(result)) {
        toast.error(
          result.message ||
            "The response could not be generated. Please try again in a few minutes.",
          { position: "top-right", autoClose: 8000 },
        );
        setGapsOpen(false);
        resetGaps();
        setGapsLoading(false);
        return;
      }
      setGapsLoading(false);
      finish(result, "info");
    } catch (err) {
      setGapsLoading(false);
      setGapsOpen(false);
      reportError(err, "Failed to generate response!");
    }
  };

  // --- Queued path --------------------------------------------------------
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollSafetyRef.current) {
      clearTimeout(pollSafetyRef.current);
      pollSafetyRef.current = null;
    }
    pollErrorsRef.current = 0;
    setPollStage(null);
    try {
      if (resumeKey) localStorage.removeItem(resumeKey);
    } catch (_) {
      /* storage can throw in private mode - ignore */
    }
  };

  const startPolling = (taskId) => {
    if (!statusUrl) return;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollSafetyRef.current) {
      clearTimeout(pollSafetyRef.current);
      pollSafetyRef.current = null;
    }
    setPollStage("Queued");
    setIssuesLoading(true);
    pollErrorsRef.current = 0;
    try {
      if (resumeKey) localStorage.setItem(resumeKey, taskId);
    } catch (_) {
      /* ignore */
    }

    const tick = async () => {
      try {
        const res = await getData(statusUrl(taskId));
        pollErrorsRef.current = 0;
        const payload = res?.data || {};

        if (payload.status === "processing") {
          setPollStage(payload.stage || "Processing");
          return;
        }
        if (payload.status === "done") {
          stopPolling();
          setIssuesLoading(false);
          const result = payload.result || {};
          // A queued run can still come back with a question, so the same
          // two steps are available here as on the direct call.
          if (result.status === "needs_input" && result.insufficient_queries) {
            setGaps(result.insufficient_queries);
            setGapsOpen(true);
            return;
          }
          if (result.status === "needs_selection") {
            setIssuesData(
              typeof result.response === "string"
                ? JSON.parse(result.response)
                : result.response,
            );
            setIssuesOpen(true);
            return;
          }
          if (!resultIsUsable(result)) {
            toast.error(
              result.message ||
                "The response could not be generated. Please try again in a few minutes.",
              { position: "top-right", autoClose: 8000 },
            );
            return;
          }
          finish(result, "success", { refetch: true });
          return;
        }
        if (payload.status === "failed") {
          stopPolling();
          setIssuesLoading(false);
          toast.error(
            `Response generation failed: ${payload.error || "unknown error"}`,
            { position: "top-right", autoClose: 6000 },
          );
        }
      } catch (err) {
        pollErrorsRef.current += 1;
        if (pollErrorsRef.current >= 3) {
          stopPolling();
          setIssuesLoading(false);
          toast.warning(
            "Connection lost - we'll resume when you reload the page.",
            { position: "top-right", autoClose: 8000 },
          );
        }
      }
    };

    tick();
    pollIntervalRef.current = setInterval(tick, 3000);
    pollSafetyRef.current = setTimeout(
      () => {
        stopPolling();
        setIssuesLoading(false);
        toast.warning("Response generation timed out. Please try again.", {
          position: "top-right",
          autoClose: 8000,
        });
      },
      15 * 60 * 1000,
    );
  };

  // Reload mid-generation: the task id is on disk, so pick it back up.
  useEffect(() => {
    if (!resumeKey || !statusUrl) return undefined;
    try {
      const stored = localStorage.getItem(resumeKey);
      if (stored) {
        toast.info("Resuming response generation", {
          position: "top-right",
          autoClose: 3000,
        });
        startPolling(stored);
      }
    } catch (_) {
      /* ignore */
    }
    // Clear timers on unmount but keep the marker, so the next mount resumes.
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (pollSafetyRef.current) {
        clearTimeout(pollSafetyRef.current);
        pollSafetyRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeKey]);

  return (
    <>
      {/* Step 1 — terms, and the legal-issue choice. */}
      <Modal
        title="Terms and Conditions"
        open={open}
        onCancel={() => {
          setAccepted(false);
          onClose?.();
        }}
        footer={null}
        centered
        closable
        maskClosable={false}
        style={{ minHeight: "500px" }}
      >
        <div style={{ maxHeight: "300px", overflowY: "auto", padding: "10px" }}>
          <TermsAndConditions placement="modal" />
        </div>

        {legalChoiceEnabled && (
          <div
            className="mt-6 p-4 border rounded-lg"
            style={{ background: "#f0f5ff" }}
          >
            <h4
              className="mb-3 font-medium"
              style={{ color: "#2746b2", fontWeight: 700 }}
            >
              Legal Issue Identification
            </h4>
            <Radio.Group
              onChange={(e) => setWithLegal(e.target.value)}
              value={withLegal}
              className="w-full"
            >
              <Space direction="vertical" className="w-full">
                <Radio value={true} className="flex items-start p-2 rounded">
                  <div className="ml-2">
                    <div className="text-sm text-gray-600 mt-1">
                      Yes, identify legal issues
                    </div>
                  </div>
                </Radio>
                <Radio value={false} className="flex items-start p-2 rounded">
                  <div className="ml-2">
                    <div className="text-sm text-gray-600 mt-1">
                      No, skip legal issue identification
                    </div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>
        )}

        <div className="mt-6">
          {/* Controlled on purpose: without `checked`, antd keeps the tick
              across open/close cycles and the next generation starts already
              consented. Consent is per generation. */}
          <Checkbox
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          >
            I accept the Terms and Conditions
          </Checkbox>
        </div>

        <div
          style={{
            textAlign: "right",
            marginTop: "10px",
            display: "flex",
            gap: "16px",
            justifyContent: "end",
          }}
        >
          <Button
            type="default"
            onClick={() => {
              setAccepted(false);
              onClose?.();
            }}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!accepted || generating}
            onClick={startGeneration}
            className="min-w-[100px]"
          >
            {generating ? <LoadingOutlined spin /> : "Generate"}
          </Button>
        </div>
      </Modal>

      {/* Step 2 — which legal issues. */}
      <Modal
        open={issuesOpen}
        onCancel={() => {
          setIssuesOpen(false);
          setSelectedIssues([]);
          setIssuesData(null);
          setWithLegal(false);
        }}
        footer={null}
        centered
        closable={false}
        maskClosable={false}
        width={800}
        style={{ minHeight: "600px" }}
      >
        <div
          style={{
            width: "100%",
            overflowX: "hidden",
            maxHeight: "500px",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <Title level={4} style={{ color: "#2746b2", marginBottom: "6px" }}>
              Add Custom Legal Issues
            </Title>
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              Add your own legal issues if any
            </Text>

            <Row gutter={8} style={{ marginBottom: "6px" }}>
              <Col flex={1}>
                <TextArea
                  placeholder="Enter your custom legal issue description..."
                  value={newCustomIssue}
                  onChange={(e) => setNewCustomIssue(e.target.value)}
                  rows={1}
                  style={{ resize: "none" }}
                  onPressEnter={(e) => {
                    if (e.ctrlKey || e.metaKey) addCustomIssue();
                  }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addCustomIssue}
                  disabled={!newCustomIssue.trim()}
                  style={{ height: "45px" }}
                >
                  Add Issue
                </Button>
              </Col>
            </Row>

            {customIssues.length > 0 && (
              <div
                style={{
                  marginBottom: "10px",
                  background: "#f8fbff",
                  border: "1px solid #e6f4ff",
                  borderRadius: "12px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid #e6f4ff",
                  }}
                >
                  <Text strong style={{ color: "#2746b2", fontSize: "15px" }}>
                    Custom Issues
                  </Text>
                  <div
                    style={{
                      background: "#2746b2",
                      color: "white",
                      padding: "4px 12px",
                      borderRadius: "16px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {customIssues.length} Added
                  </div>
                </div>

                <div style={{ maxHeight: "220px", overflowY: "auto", paddingRight: 4 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {customIssues.map((issue, index) => (
                      <div
                        key={issue.uid}
                        style={{
                          background: "#f0f8ff",
                          border: "2px solid #1890ff",
                          borderRadius: "8px",
                          padding: "16px",
                        }}
                      >
                        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                          <div
                            style={{
                              minWidth: "28px",
                              height: "28px",
                              background: "#2746b2",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {(index + 1).toString().padStart(2, "0")}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <TextArea
                              value={issue.custom_issue}
                              onChange={(e) =>
                                editCustomIssue(issue.uid, e.target.value)
                              }
                              rows={2}
                              placeholder="Describe your custom legal issue..."
                              style={{
                                background: "#fff",
                                border: "1px solid #e6f4ff",
                                borderRadius: "6px",
                                resize: "none",
                                fontSize: "14px",
                              }}
                            />
                          </div>
                          <Button
                            type="text"
                            icon={<DeleteOutlined style={{ fontSize: 14 }} />}
                            onClick={() => removeCustomIssue(issue.uid)}
                            style={{
                              color: "#ff4d4f",
                              background: "#fff2f0",
                              width: "32px",
                              height: "32px",
                              borderRadius: "6px",
                              flexShrink: 0,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Divider style={{ margin: "5px" }} />

          <Title level={4} style={{ color: "#2746b2", marginBottom: "5px" }}>
            Select from Identified Legal Issues
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 10 }}>
            Choose from the automatically identified legal issues:
          </Text>

          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {issuesData?.issues?.map((issue) => {
              const picked = selectedIssues.some((s) => s.uid === issue.uid);
              return (
                <Card
                  key={issue.uid}
                  size="small"
                  style={{
                    border: picked ? "2px solid #1890ff" : "1px solid #d9d9d9",
                    backgroundColor: picked ? "#f0f8ff" : "white",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <Checkbox
                      checked={picked}
                      onChange={(e) => toggleIssue(issue.uid, e.target.checked)}
                      style={{ marginTop: "2px" }}
                    />
                    <div style={{ flex: 1 }}>
                      <Title level={5} style={{ margin: "0 0 8px 0", color: "#2746b2" }}>
                        {issue.issue_description}
                      </Title>
                      <div style={{ marginBottom: "8px" }}>
                        <Text strong>Issue Related Notice Point: </Text>
                        <Text>{issue.related_notice_point}</Text>
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <Text strong>Defense Strategy: </Text>
                        <Text>{issue.defense_strategy}</Text>
                      </div>
                      {issue.taxpayer_state && (
                        <div style={{ marginBottom: "8px" }}>
                          <Text strong>Taxpayer State: </Text>
                          <Text>{issue.taxpayer_state}</Text>
                        </div>
                      )}
                      {issue.assessment_year && (
                        <div style={{ marginBottom: "8px" }}>
                          <Text strong>Assessment Year: </Text>
                          <Text>{issue.assessment_year}</Text>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </Space>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fafafa",
            margin: "20px -24px -24px -24px",
            padding: "20px 24px",
          }}
        >
          <div>
            <Text strong style={{ display: "block", marginBottom: "4px" }}>
              Total Selected Issues: {totalSelected}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Predefined: {selectedIssues.length} | Custom:{" "}
              {customIssues.filter((i) => i.custom_issue.trim()).length}
            </Text>
          </div>
          <Space>
            <Button
              onClick={() => {
                setIssuesOpen(false);
                setSelectedIssues([]);
                setIssuesData(null);
                setWithLegal(false);
              }}
              disabled={issuesLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={totalSelected === 0 || issuesLoading}
              onClick={generateWithSelectedIssues}
              className="min-w-[120px]"
            >
              {issuesLoading ? (
                <span>
                  <LoadingOutlined spin />
                  {pollStage ? <span style={{ marginLeft: 8 }}>{pollStage}</span> : null}
                </span>
              ) : (
                "Generate Response"
              )}
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Step 3 — what the engine says is missing. */}
      <Modal
        title="Additional Information Required"
        open={gapsOpen}
        onCancel={() => {
          setGapsOpen(false);
          resetGaps();
        }}
        footer={null}
        centered
        closable={!gapsLoading}
        maskClosable={false}
        width={700}
      >
        <div style={{ padding: "10px" }}>
          <div style={{ marginBottom: "16px" }}>
            <Text type="secondary">
              Some queries require additional information. You can provide the
              missing details below, or consider the already available data.
            </Text>
          </div>

          <div style={{ maxHeight: "400px", overflowY: "auto", marginBottom: 20 }}>
            {gaps.map((query, index) => {
              const isWaived = waived.includes(query.point_number);
              return (
                <div
                  key={query.point_number || index}
                  style={{
                    marginBottom: "16px",
                    padding: "16px",
                    border: isWaived ? "1px solid #52c41a" : "1px solid #e8e8e8",
                    borderRadius: "8px",
                    backgroundColor: isWaived ? "#f6ffed" : "#fafafa",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ color: "#1890ff" }}>
                      Point {query.point_number}: {query.query_text}
                    </Text>
                  </div>

                  {query.available_info && (
                    <div style={{ marginBottom: "8px" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        <strong>Available:</strong> {query.available_info}
                      </Text>
                    </div>
                  )}
                  {query.missing_info && (
                    <div style={{ marginBottom: "8px" }}>
                      <Text style={{ fontSize: "12px", color: "#fa8c16" }}>
                        <strong>Missing:</strong> {query.missing_info}
                      </Text>
                    </div>
                  )}

                  {isWaived ? (
                    <div>
                      <div
                        style={{
                          marginBottom: "8px",
                          padding: "8px",
                          backgroundColor: "#e6fffb",
                          borderRadius: "4px",
                        }}
                      >
                        <Text style={{ color: "#52c41a", fontSize: "12px" }}>
                          Response will be generated using already available
                          information for this query.
                        </Text>
                      </div>
                      <Button
                        size="small"
                        onClick={() => toggleWaive(query.point_number)}
                        disabled={gapsLoading}
                      >
                        Undo - Provide Additional Info
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <TextArea
                        placeholder="Enter additional information for this query..."
                        value={answers[query.point_number] || ""}
                        onChange={(e) =>
                          answerGap(query.point_number, e.target.value)
                        }
                        rows={2}
                        style={{ marginBottom: "8px" }}
                        disabled={gapsLoading}
                      />
                      <Button
                        size="small"
                        onClick={() => toggleWaive(query.point_number)}
                        disabled={gapsLoading}
                        style={{ color: "#1890ff", borderColor: "#1890ff" }}
                      >
                        Consider already available data
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: "16px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              onClick={() => submitGaps({ waiveAll: true })}
              disabled={gapsLoading}
              style={{ minWidth: "280px" }}
            >
              {gapsLoading ? (
                <LoadingOutlined spin />
              ) : (
                "Consider already available data for all queries"
              )}
            </Button>
            <Button
              type="primary"
              onClick={() => submitGaps()}
              disabled={
                gapsLoading ||
                (Object.values(answers).every((v) => !v?.trim()) &&
                  waived.length === 0)
              }
            >
              {gapsLoading ? <LoadingOutlined spin /> : "Submit"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GenerateResponseFlow;
